import styled from '@emotion/styled';
import { rem } from '@utils';

const Wrapper = styled.figure(
  ({ theme }) => `
  background: ${theme.palette.common.white};
  box-shadow: inset 0 0 0 1px ${theme.palette.grey['200']};
  border-radius: 8px;
  margin: 0 auto;
  max-width: var(--insight-visualization-max-width);
  padding: ${rem(16)} ${rem(24)};
  width: 100%;

  .show-on-export {
    display: none;
  }

  .dropdown-menu {
    background: var(--dropdown-menu-icon-background-color);
    color: var(--dropdown-menu-icon-color);

    svg {
      fill: var(--dropdown-menu-icon-color);
    }

    &:hover {
      background: inherit !important;

      svg {
        fill: ${theme.palette.primary.main};
      }
    }
  }

  .button {
    height: auto;
    text-align: left;
    align-items: flex-start;
  }

  @media (min-width: 768px) and (max-width: 1440px) {
    .button {
      width: 100%;
    }
  }

  @media (max-width: 430px) {
    padding: ${rem(16)};

    .button {
      width: 100%;
    }
  }
`
);

const Title = styled.h4`
  color: var(--insight-visualization-title-color);
  font-size: inherit;
  font-weight: 600;
  margin-bottom: ${rem(8)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Content = styled.div`
  display: flex;
  gap: ${rem(8)};
  line-height: ${rem(18)};
  margin-bottom: ${rem(12)};
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.main};
    fill: ${({ theme }) => theme.palette.primary.main};
    transition: 250ms ease;
  }

  .icon {
    flex-shrink: 0;
  }
`;

const Table = styled.div`
  border: 1px solid var(--insight-visualization-table-border-color);
  border-radius: 4px;

  @media (max-width: 1440px) {
    margin: 0 auto ${rem(12)};
    width: var(--insight-visualization-table-width);
  }

  @media (max-width: 768px) and (min-width: 636px) {
    width: auto;
  }

  @media (max-width: 430px) {
    width: 100%;
  }
`;

const TableRow = styled.div`
  border-bottom: 1px solid var(--insight-visualization-table-border-color);
  display: flex;
  position: relative;
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  width: 50%;

  & + * {
    border-left: 1px solid var(--insight-visualization-table-border-color);
  }

  @media (max-width: 1440px) {
    border: none;
    flex-basis: 100%;

    &:first-of-type {
      display: none;
    }
  }

  @media (max-width: 768px) and (min-width: 636px) {
    flex-basis: auto;

    &:first-of-type {
      display: flex;
    }

    & + * {
      border-left: 1px solid var(--insight-visualization-table-border-color) !important;
    }
  }
`;

const TableHeader = styled(TableCell)<{
  alignContent?: 'left' | 'center' | 'right';
}>`
  font-weight: 600;
  padding: ${rem(4)} ${rem(8)};
  justify-content: ${({ alignContent }) =>
    (alignContent === 'center' && 'center') ||
    (alignContent === 'right' && 'flex-end') ||
    'flex-start'};
  height: ${rem(40)};

  & > * {
    flex-basis: max-content;
  }
`;

const TableData = styled(TableCell)`
  padding: 0;
`;

const TableFooter = styled.div`
  color: var(--insight-visualization-caption-color);
  display: flex;
  flex-flow: column;
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  padding: ${rem(8)};
  row-gap: ${rem(8)};
`;

const SelectedDataPoint = styled.div`
  width: 100%;
`;

const Keys = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(4)};
`;

const KeysLabel = styled.span`
  font-weight: 600;
  white-space: nowrap;
  height: 100%;

  &.fixed-height {
    display: inline-flex;
    align-items: center;
    height: ${rem(24)};
  }
`;

const DataKeyList = styled.ul`
  display: inline-flex;
  gap: ${rem(8)};
  flex-wrap: wrap;
  overflow: hidden;
  width: 100%;
`;

const Caption = styled.div<{ placement?: 'top' | 'bottom' }>(
  ({ placement }) => `
  color: var(--insight-visualization-caption-color);
  margin-bottom: ${placement === 'top' ? rem(-4) : 0};
  margin-top: ${placement === 'top' ? rem(4) : 0};

  ${Content} + & {
    margin-top: ${rem(16)};
    padding: 0;
  }
`
);

const DataList = styled.ol`
  align-self: flex-start;
  flex-grow: 1;
  width: inherit;
`;

const DataListItem = styled.li`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: flex-start;
  min-height: ${rem(28)};
  max-height: ${rem(36)};
  list-style: none;
  padding: ${rem(4)} ${rem(8)};
  white-space: nowrap;
`;

const DataTitle = styled.div`
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const DataCaption = styled.div`
  color: var(--insight-visualization-caption-color);
  font-size: ${rem(10)};
  line-height: ${rem(13)};
  overflow: hidden;
  text-overflow: ellipsis;  
  width: 100%;
`;

const Separator = styled.div<{
  x: number | `${number}%`;
  y: number | `${number}%`;
}>`
  background: var(--chart-grid-color);
  height: 1px;
  position: absolute;
  top: ${({ y }) => (typeof y === 'string' ? y : rem(y))};
  left: ${({ x }) => (typeof x === 'string' ? x : rem(x))};
  bottom: 0;
  right: 0;
  z-index: 1;
`;

const Notice = styled.div(
  ({ theme }) => `
  background: var(--chat-message-reply-background-color);
  border-radius: 4px;
  border: 1px dashed ${theme.palette.grey['400']};
  color: var(--insight-visualization-notice-color);
  font-size: ${rem(12)};
  font-weight: 600;
  line-height: ${rem(16)};
  margin: ${rem(16)} 0;
  text-align: center;
  padding: ${rem(20)};
`
);

const Footer = styled.footer`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${rem(24)};
  gap: ${rem(16)} ${rem(8)};
  justify-content: space-between;

  .dropdown-menu {
    width: auto;
  }
`;

const ExportedContent = styled.div<{ width: number }>(
  ({ width, theme }) => `
  background: ${theme.palette.common.white};
  border: 1px solid ${theme.palette.grey['200']};
  padding: ${rem(24)};
  position: absolute;
  right: -99999px;
  width: ${width}px;
  z-index: -1;

  .audience-chip {
    background: transparent;
    box-shadow: none;
    margin-left: ${rem(8)};

    .audience-chip-content {
      gap: 0 ${rem(8)};
      padding: ${rem(4)} 0;
    }
  }

  .hide-on-export {
    display: none;
  }

  .show-on-export {
    display: block;
  }

  ${Title} {
    font-size: ${rem(20)};
    line-height: ${rem(32)};
  }

  ${Content} {
    font-size: ${rem(16)};
    line-height: ${rem(24)};
  }

  .insight-visualization-question {
    flex-grow: 1;
    margin-top: -${rem(4)};
  }
`
);

const SourceInfo = styled.div`
  display: flex;
  margin-top: ${rem(24)};
  width: 100%;
`;

const MetaList = styled.div`
  color: ${({ theme }) => theme.palette.grey['600']};
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${rem(8)};
  flex-grow: 1;
`;

const MetaLabel = styled.div`
  font-weight: 600;
  white-space: nowrap;
`;

const MetaValue = styled.div();

const Logo = styled.div`
  align-self: flex-end;
  margin-left: ${rem(40)};
`;

const MetadataGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const TooltipContent = styled.div`
  display: flex;
  column-gap: ${rem(8)};
`;

export default {
  Caption,
  Content,
  DataKeyList,
  DataList,
  DataListItem,
  DataTitle,
  DataCaption,
  ExportedContent,
  Footer,
  Keys,
  KeysLabel,
  Logo,
  MetadataGroup,
  MetaList,
  MetaLabel,
  MetaValue,
  Notice,
  SelectedDataPoint,
  Separator,
  SourceInfo,
  Table,
  TableRow,
  TableHeader,
  TableData,
  TableFooter,
  Title,
  TooltipContent,
  Wrapper
};
